import { ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CustomColumnModel, DynamicReportGeneralModel, ParameterModel } from '../dynamic-report-general.model';
import { DYNAMIC_REPORT_HTTP_LOADER, DynamicReportHttpLoader } from '../dynamic-report-http-loader.service';
import { BehaviorSubject, Observable, Subject, combineLatest, of, startWith, switchMap, takeUntil } from 'rxjs';
import { IDynamicReportType } from '../dynamic-report-general.interface';
import { LocalSearcher2, Searcher } from '@shared/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { cloneDeep } from 'lodash-es';
import { SORT_TYPE_OPTIONS, TYPE_SEARCH_OPTIONS } from '../dynamic-report.data ';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSelectChange } from '@angular/material/select';
import { ToastService } from '@shared/components/toast';
import { MatOptionSelectionChange } from '@angular/material/core';
import { isMoment } from 'moment';
import { format } from 'date-fns';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { RedrValidator } from '@red/utils';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'red-dynamic-report-general-form',
  templateUrl: './dynamic-report-general-form.component.html',
  styleUrls: ['./dynamic-report-general-form.component.scss']
})
export class DynamicReportGeneralFormComponent implements OnInit {
  @Input() reportTypes: IDynamicReportType[] = [];
  @Input() item?: DynamicReportGeneralModel;
  @Input() isDetail?: boolean = false;

  form!: UntypedFormGroup;
  loadingMetadata = false;
  isPreview = false;

  private _unsubscribeAll: Subject<void> = new Subject<void>();
  metadata$ = new BehaviorSubject<DynamicReportGeneralModel | null>(null);

  customColumnsMetaData: CustomColumnModel[] = [];
  colsFilter: CustomColumnModel[] = [];
  parameterMetadata: ParameterModel[] = [];

  typeSearchOptions = cloneDeep(Object.values(TYPE_SEARCH_OPTIONS));
  sortTypeOptions = cloneDeep(Object.values(SORT_TYPE_OPTIONS));

  constructor(
    @Inject(DYNAMIC_REPORT_HTTP_LOADER) private _apiService: DynamicReportHttpLoader,
    private _formBuilder: UntypedFormBuilder,
    private _cdr: ChangeDetectorRef,
    private _toastService: ToastService,
    private _elementRef: ElementRef
  ) {
  }
  columnsLocalSearcher = new LocalSearcher2(CustomColumnModel, ['column', 'displayName', 'isFilterable', 'isSortable'], this.getCustomColumns(null));
  columnsSearcher = new Searcher<CustomColumnModel>(filter => this.columnsLocalSearcher.filter({ ...filter, limit: 500 }), this._unsubscribeAll);

  parametersLocalSearcher = new LocalSearcher2(ParameterModel, ['column', 'displayName', 'type', 'operators'], this.getParameters(null));
  parametersSearcher = new Searcher<ParameterModel>(filter => this.parametersLocalSearcher.filter({ ...filter, limit: 500 }), this._unsubscribeAll);

  get columnFormArray() {
    return this.form.get('customColumns') as FormControl<any>;
  }
  // columns = new BehaviorSubject<AbstractControl[]>([]);
  // columnsSelection = new SelectionModel<CustomColumnModel>(true, []);
  columnsSelection: CustomColumnModel[] = [];
  sequenceColumns = this.columnsSelection?.length > 0 ? this.columnsSelection?.length + 1 : 1;

  get parameterFormArray() {
    return this.form.get('parameters') as FormArray;
  }
  parameters = new BehaviorSubject<AbstractControl[]>([]);
  parametersSelection = new SelectionModel<string>(true, []);


  ngOnInit(): void {
    this.form = this.createForm(this.item);
    this.registryChangeDependencies();
    this.updateParameters();
    if (this.item) {
      this._apiService.getMeta(this.item?.reportType).subscribe(res => {
        this.metadata$.next(res);
        this.customColumnsMetaData = res?.customColumns ?? [];
        this.columnsLocalSearcher = new LocalSearcher2(
          CustomColumnModel,
          ['column', 'displayName'],
          this.getCustomColumns(res?.customColumns as CustomColumnModel[])
        );
        this.columnsSearcher.forceSearch();
      });
      this.columnsSelection = (this.item?.customColumns as CustomColumnModel[])?.map((e, i) => {
        return { ...e, sequence: i + 1 }
      });
      this.colsFilter = this.item?.customColumns?.filter((e) => e.isSortable) ?? [];

    }

    if (this.isDetail) {
      this.form.disable({ emitEvent: false });
      this.isPreview = true;
    }

  }
  createForm(item?: DynamicReportGeneralModel): UntypedFormGroup {
    return this._formBuilder.group({
      name: [item?.name, [Validators.required, RedrValidator.maxChar(200), RedrValidator.excelFileNameValidator()]],
      reportType: [{ value: item?.reportType, disabled: this.item ? true : false }, [Validators.required]],
      description: [item?.description, []],
      customColumns: [item?.customColumns, [Validators.required, this.validateCustomColumns.bind(this)]],
      parameters: this._formBuilder.array(item?.parameters?.map(param => this.createParameterForm(param as ParameterModel)) || [], []),
      config: this._formBuilder.group({
        orderBy: [item?.config?.orderBy],
        orderType: [item?.config?.orderType ?? 'DESC'],
        page: [item?.config?.page ?? 1],
        limit: [20],
        combineType: [item?.config?.combineType ?? 'AND']
      }),
    });
  }

  createParameterForm(param?: ParameterModel) {
    const formGroup = this._formBuilder.group({
      param: [{ alias: param?.alias, column: param?.column, displayName: param?.displayName }, []],
      displayName: [param?.displayName, []],
      alias: [param?.alias, []],
      column: [param?.column, [Validators.required]],
      columnType: [param?.columnType, []],
      type: [param?.type, []],
      value: [param?.value, [Validators.required]],
      valueType: [param?.valueType, []],
      default: [param?.default, []],
      options: [param?.options, []],
      isVisible: [param?.isVisible, []],
      templateRef: [param?.templateRef, []],
      isEditable: [param?.isEditable, []],
      operator: [param?.operator, [Validators.required]],
      operators: [param?.operators, []],
      range: this._formBuilder.group({ start: param?.value?.start, end: param?.value?.end }),
      numberOfValues: [param?.value && Array.isArray(param?.value) ? param?.value?.length : 0],
      tooltipValues: [param?.value && Array.isArray(param?.value) ? param?.value?.map((e: any) => e?.label).join(', ') : ''],
    });
    formGroup.get('value')?.valueChanges?.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
      formGroup.get('tooltipValues')?.patchValue(value && Array.isArray(value) ? value?.filter((e: any) => !!e)?.map((e: any) => e?.label).join(',\n') : '', { emitEvent: false });

      formGroup.get('numberOfValues')?.patchValue(value && Array.isArray(value) ? value?.filter((e: any) => !!e)?.length : 0, { emitEvent: false });
    });

    return formGroup
  };
  registryChangeDependencies() {
    this.form.get('reportType')?.valueChanges?.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
      this.form.get('customColumns')?.patchValue(null, { emitEvent: false });
      if (!value) return;

      this._apiService.getMeta(value).subscribe((data: DynamicReportGeneralModel) => {
        this.customColumnsMetaData = data?.customColumns ?? [];
        this.columnsLocalSearcher = new LocalSearcher2(
          CustomColumnModel,
          ['column', 'displayName'],
          value && data?.customColumns ? this.getCustomColumns(data?.customColumns) : []
        );
        this.columnsSearcher.forceSearch();
        this.metadata$.next(data)
      });
    });

    combineLatest([
      this.form.get('customColumns')?.valueChanges.pipe(startWith(this.form.get('customColumns')?.value)) ?? of(null),
      this.metadata$,
    ]).pipe(takeUntil(this._unsubscribeAll)).subscribe(([value, metadata]) => {
      this.updateParameters();
      this.colsFilter = value?.filter((e: CustomColumnModel) => e?.isSortable) ?? [];
      this.form.get('parameters')?.patchValue(null, { emitEvent: false });
      this.parametersLocalSearcher = new LocalSearcher2(
        ParameterModel,
        ['column', 'displayName'],
        value ? this.getParameters(value, metadata?.parameters as ParameterModel[]) : []
      );
      this.parametersSearcher.forceSearch();

    });

    this.form?.valueChanges?.pipe(takeUntil(this._unsubscribeAll)).subscribe((v) => {
      // const firstPreviewControl: HTMLElement = this._elementRef.nativeElement.querySelector('.filter-dynamic-report');
      // firstPreviewControl.scrollIntoView({
      //   behavior: 'smooth',
      //   block: 'center',
      // });
      setTimeout(
        () => {
          this.isPreview = false;
        },
        500
      );

    });

  }


  addColumn(event: MatOptionSelectionChange, item: CustomColumnModel | null, isCheckAll: boolean = false) {
    this.sequenceColumns = this.columnsSelection?.length > 0 ? this.columnsSelection?.length + 1 : 1;
    if (event.isUserInput) {
      this.form.get('customColumns')?.setValue(null);
      if (isCheckAll && !item) {
        if (event.source.selected) {
          this.columnsSelection = this.customColumnsMetaData?.map((e: CustomColumnModel, index: number) => {
            return { ...e, sequence: index + 1 }
          });
        } else {
          // Remove deselected All
          this.columnsSelection = [];
        }
      } else {
        if (event.source.selected) {
          // Add with sequence if not already in selectedItems
          const exists = this.columnsSelection.find((x: CustomColumnModel) => x.column === item?.column && x.alias === item?.alias);
          if (!exists) {
            this.columnsSelection.push({ ...item as CustomColumnModel, sequence: this.sequenceColumns++ });
          }
        } else {
          // Remove deselected item
          this.columnsSelection = this.columnsSelection.filter((x: CustomColumnModel) => !(x.column === item?.column && x.alias === item?.alias))?.map((e, i) => {
            return { ...e, sequence: i + 1 };
          });
          const paramIndex = this.parameterFormArray.value.findIndex((x: ParameterModel) => x.column === item?.column && x.alias === item?.alias);
          this.removeFilter(paramIndex);
        }
      }
      this.form.get('customColumns')?.setValue(this.columnsSelection);
    }
  }

  removeColumn(item: CustomColumnModel) {
    this.form.get('customColumns')?.setValue(null);
    // Remove deselected item
    this.columnsSelection = this.columnsSelection.filter(x => !(x.column === item?.column && x.alias === item?.alias))?.map((e, i) => {
      return { ...e, sequence: i + 1 };
    });
    this.form.get('customColumns')?.setValue(this.columnsSelection);
  }

  addParameter(event: MatSelectChange, formControl: AbstractControl | null | undefined) {
    if (!event.value) return;

    formControl?.patchValue(event?.value);
    this.updateParameters();
  };


  getCustomColumns(customColumns: CustomColumnModel[] | null): Observable<CustomColumnModel[]> {
    if (customColumns) {
      return of((customColumns ?? []) as CustomColumnModel[])
    } else if (this.item) {
      return this.metadata$?.pipe(switchMap((data: DynamicReportGeneralModel | null) => {
        this.customColumnsMetaData = data?.customColumns ?? [];
        // this.parameterMetadata = data?.parameters ?? [];
        return of(data?.customColumns ?? [] as CustomColumnModel[])
      }));
    } else {
      return of([] as CustomColumnModel[])
    }
  };


  getParameters(cols: CustomColumnModel[] | null, parameters: ParameterModel[] = []) {
    const type = this.form?.get('reportType')?.value;
    const colsFilter = cols?.map(e => `${e?.alias}.${e?.column}`) ?? [];
    if (type && colsFilter?.length > 0 && parameters?.length > 0) {
      return of((parameters?.filter(e => colsFilter?.includes(`${e?.alias}.${e?.column}`)) ?? []) as ParameterModel[])
    } else {
      return of([]);
    }
  }
  checkParamsSection(column: string) {
    return !this.parametersSelection.selected?.includes(column);
  }

  addAllMultiSelections(event: MatCheckboxChange, formControl: AbstractControl) {
    if (event.checked) {
      const options = formControl.get('options')?.value ?? [];
      formControl.get('value')?.setValue(options);
    } else {
      formControl.get('value')?.setValue([]);
    }
  }
  valueControl(form: AbstractControl): FormControl {
    return form.get('value') as FormControl;
  }
  dropColumn(event: CdkDragDrop<any[]>) {
    if (!event || (event?.previousIndex === event?.currentIndex)) {
      return;
    }
    const objectCols: any = (this.columnsSelection as CustomColumnModel[]).reduce((a, v, i) => ({ ...a, [i + 1]: { ...v, sequence: i + 1 } }), {});
    const dir = event?.currentIndex + 1 > event?.previousIndex + 1 ? 1 : -1;
    const from = event?.previousIndex + 1;
    const to = event?.currentIndex + 1;
    let previous = objectCols[from];
    for (let i = from; i * dir < to * dir; i = i + dir) {
      let current = objectCols[i + dir];
      current = { ...current, sequence: i };
      objectCols[i] = current;
    }
    previous = { ...previous, sequence: to };
    objectCols[to] = previous;

    this.isPreview = false;
    this.columnsSelection = Object.values(objectCols);
    this.form.get('customColumns')?.setValue(Object.values(objectCols), { emitEvent: false })
  }

  changeSequenceColumn(event: any, col: CustomColumnModel, index: number) {
    const newSequence = (event?.target?.value * 1);
    if (newSequence < 0 || newSequence > this.columnsSelection.length) {
      this._toastService.open({ type: 'warning', title: 'Warning', messages: 'The input value must be greater than 0 and cannot exceed the total number of items.' });
      return;
    }
    const objectCols: any = (this.columnsSelection as CustomColumnModel[]).reduce((a, v, i) => ({ ...a, [i + 1]: { ...v } }), {});
    objectCols[index] = { ...col, sequence: newSequence };
    this.columnsSelection = Object.values(objectCols);
    //this.columnsSelection = this.columnsSelection.filter(x => !(x.column === col?.column && x.alias === col?.alias));
  };

  sortColumn() {
    if (this.columnsSelection && this.columnsSelection.length > 0) {
      const transformed = cloneDeep(this.columnsSelection).sort((a, b) => {
        const seqA = a?.sequence;
        const seqB = b?.sequence;
        if (seqA && seqB) return seqA - seqB;
        return 0;
      });
      const objectCols: any = (transformed as CustomColumnModel[]).reduce((a, v, i) => ({ ...a, [i + 1]: { ...v, sequence: i + 1 } }), {});
      this.columnsSelection = Object.values(objectCols);
      this.form.get('customColumns')?.setValue(Object.values(objectCols), { emitEvent: false })
    }
  }

  // changeSequenceColumn(event?: any, previousValue?: number) {
  //   const objectCols: any = (this.columnsSelection as CustomColumnModel[]).reduce((a, v, i) => ({ ...a, [i + 1]: { ...v, sequence: i + 1 } }), {});
  //   const currentIndex = (event?.target?.value * 1) - 1;
  //   const previousIndex = (previousValue ?? 0) * 1;


  //   if (!event || previousIndex === currentIndex) {
  //     return;
  //   }
  //   if (previousIndex < 0 || previousIndex > this.columnsSelection.length) {
  //     this._toastService.open({ type: 'danger', title: 'Failed', messages: 'You can note input' });
  //     return;
  //   }

  //   setTimeout(() => {
  //     console.log('--------', previousIndex, currentIndex);
  //     const dir = currentIndex + 1 > previousIndex + 1 ? 1 : -1;
  //     const from = previousIndex + 1;
  //     const to = currentIndex + 1;
  //     let previous = objectCols[from];
  //     console.log('--------from', from, to);

  //     for (let i = from; i * dir < to * dir; i = i + dir) {
  //       let current = objectCols[i + dir];
  //       current = { ...current, sequence: i + 1 };
  //       objectCols[i] = current;
  //     }
  //     previous = { ...previous, sequence: to + 1 };
  //     objectCols[to] = previous;

  //     //const current = objectCols[currentIndex];
  //     //objectCols[previousIndex] = { ...objectCols[currentIndex], sequence: currentIndex };
  //     //objectCols[previousIndex] = { ...current, sequence: previousIndex };

  //     this.isPreview = false;
  //     this.columnsSelection = Object.values(objectCols);
  //     console.log('--------this.columnsSelection', this.columnsSelection);

  //     this.form.get('customColumns')?.setValue(Object.values(objectCols), { emitEvent: false });
  //   }, 1000);

  // }
  previewReport() {
    if (this.invalid()) {
      this.isPreview = false;
      return;
    };
    this.isPreview = true;
  };

  // Add - Remove Data
  addNewFilter(): void {
    const formItem = this.createParameterForm();
    this.parameterFormArray.push(formItem);
    this.updateParameters();
  }

  removeFilter(index: number): void {
    this.parameterFormArray.removeAt(index);
    this.updateParameters();
  }

  updateParameters() {
    this.parametersSelection.clear();
    this.parameterFormArray?.getRawValue().forEach((item: any) => item?.column && this.parametersSelection.select(`${item?.alias}.${item?.column}`));

    this.parameters.next(this.parameterFormArray?.controls ?? []);

    setTimeout(() => this._cdr.detectChanges());
  }

  convertValueDateTime(val: any): string {
    if (isMoment(val)) {
      return val.format('YYYY-MM-DD');
    }
    if (val instanceof Date) {
      return format(val, 'yyyy-MM-dd');
    }
    return val ?? null;
  }
  updateDateRange(formControl: AbstractControl | null | undefined) {
    if (formControl?.get('range')?.value?.start || formControl?.get('range')?.value?.end) {
      formControl?.get('value')?.setValue({ start: this.convertValueDateTime(formControl?.get('range')?.value?.start), end: this.convertValueDateTime(formControl?.get('range')?.value?.end) }, { emitEvent: false });
    }
  }

  onDateRangeChange(event: MatDatepickerInputEvent<Date>, formControl: AbstractControl | null | undefined, type: 'start' | 'end') {
    const start = formControl?.get('range')?.value?.start;
    const end = formControl?.get('range')?.value?.end;
    if (type === 'start') {
      formControl?.get('value')?.patchValue({ start: this.convertValueDateTime(event.value), end: this.convertValueDateTime(end) }, { emitEvent: false });
    } else {
      formControl?.get('value')?.patchValue({ start: this.convertValueDateTime(start), end: this.convertValueDateTime(event.value) }, { emitEvent: false });
    }
  }

  compareWithColumn(ob1: any, ob2: any): boolean {
    return ob1?.column === ob2?.column && ob1?.alias === ob2?.alias;
  }
  compareWithParam(ob1: any, ob2: any): boolean {
    return ob1?.id === ob2?.id;
  }
  compareWithValueFilter(ob1: any, ob2: any): boolean {
    return ob1?.value === ob2?.value;
  }

  validateCustomColumns(control: AbstractControl): ValidationErrors | null {
    const length = control?.value?.length ?? 0;
    return length > 30 ? { maxColumns: true } : null;
  }

  invalid(): boolean {
    if (this.form.invalid) {
      this.scrollToFirstInvalidControl();
      return true;
    }
    if (this.parameterFormArray.getRawValue()?.length < 1) {
      this.scrollToFirstInvalidControl('.filter-dynamic-report');
      this._toastService.open({ type: 'danger', title: 'Failed', messages: 'You must enter at least one search criteria in the filter to save the report.' });
      return true;
    }
    return false;
  }

  getRawValue() {
    if (this.invalid()) {
      return;
    };
    // if (this.form.invalid) {
    //   this.scrollToFirstInvalidControl();
    //   return;
    // }
    // if (this.parameterFormArray.getRawValue()?.length < 1) {
    //   this.scrollToFirstInvalidControl('.filter-dynamic-report');
    //   this._toastService.open({ type: 'danger', title: 'Failed', messages: 'You must enter at least one search criteria in the filter to save the report.' });
    //   return;
    // }
    return this.form.getRawValue();
  }

  scrollToFirstInvalidControl(nameClass: string = '.ng-invalid:not(form, mat-vertical-stepper)'): void {
    const firstInvalidControl: HTMLElement = this._elementRef.nativeElement.querySelector(nameClass);
    if (firstInvalidControl) {
      setTimeout(
        () =>
          firstInvalidControl.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          }),
        200
      );
    }
  }
}
